import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Layout } from 'antd-v5';
import { DateTime } from 'luxon';
import Recaptcha from 'react-google-recaptcha';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button as ButtonV5, Heading, Text } from 'scouting-ui/lib';

import BSALogo from '@assets/images/BSAColorLogo.svg';
import scoutbookapp2 from '@assets/images/scoutbook-app-2.png';
import scoutbookapp4 from '@assets/images/scoutbook-app-4.png';
import scoutbookapp from '@assets/images/scoutbook-app.png';
import {
  API_SWITCH_DEV,
  DEV_MASQUERADE_LOGIN,
  GOOGLE_RECAPTCHA_INVI_SITE_KEY,
  LOGIN_CAPTCHA,
  VERSION,
  getMyScoutingUrl,
} from '@config/config';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  ApiSelector,
  Button,
  Card,
  Col,
  FlexSpacer,
  Input,
  OfflineIcon,
  Row,
  S,
  T,
} from '@shared/components';
import { appName } from '@shared/constants';
import { offlineSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import FleurDeLisIcon from '../../../../assets/images/FleurDeLis.svg';
import { loginRequest } from '../../duck/actions';
import { useLogin } from '../../duck/hooks';
import { loginLoadingSel, userDataLoadingSel } from '../../duck/selectors';
import ScoutbookAd from '../ScoutbookAd';
import AppleSignInButton from './AppleSignInButton';
import DevMasqueradeFields from './DevMasqueradeFields';
import GoogleSignInButton from './GoogleSignInButton';
import styles from './LoginPage.less';
import { useDevMasquerade } from './useDevMasquerade';

const { Header, Content, Footer } = Layout;

const captchaEnabled =
  LOGIN_CAPTCHA && !featureFlags.getFlag('SBL_6061_DISABLE_RECAPTCHA');
const RECOVER_URL = `${getMyScoutingUrl()}/recover-credentials`;

export const LoginPage: React.FC<{
  loading: boolean;
  offline: boolean;
  onLogin: (login: string, password: string, recaptchaToken?: string) => void;
  masqueradeMode?: boolean;
  masqueradeMemberId?: string;
  onMasqueradeModeChange?: (checked: boolean) => void;
  onMasqueradeMemberIdChange?: (memberId: string) => void;
}> = props => {
  const [state, setState] = useState<{
    login: string;
    password: string;
    isPasswordVisible: boolean;
    captchaValue: string | null;
    redirecting: boolean;
  }>({
    login: '',
    password: '',
    isPasswordVisible: false,
    captchaValue: null,
    redirecting: false,
  });
  const captchaRef = useRef<Recaptcha>(null);
  const passwordInputRef = useRef<HTMLInputElement>();
  const googleSiteKey = GOOGLE_RECAPTCHA_INVI_SITE_KEY || '';

  useEffect(() => {
    passwordInputRef.current?.focus();
  }, [state.isPasswordVisible]);

  const isValidForm = () => {
    if (props.masqueradeMode) {
      return !!props.masqueradeMemberId;
    }
    return state.login && state.password;
  };

  const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isValidForm()) return;

    let recaptchaToken: string | undefined;

    if (captchaEnabled) {
      if (!googleSiteKey) {
        toastService.error(<FormattedMessage id="user.recaptcha.error" />);
        return;
      }

      const token = await captchaRef.current?.executeAsync();

      if (!token) {
        return;
      }
      recaptchaToken = token;
      captchaRef.current?.reset();
    }

    // In masquerade mode, onLogin is handled by the hook (credentials from env vars)
    if (props.masqueradeMode) {
      props.onLogin('', '', recaptchaToken);
      return;
    }

    props.onLogin(state.login, state.password, recaptchaToken);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const target = event.target.name;
    setState(prev => ({
      ...prev,
      [target]: value,
    }));
  };

  const handleTogglePasswordVisibilityClick = () => {
    setState(({ isPasswordVisible, ...prev }) => ({
      ...prev,
      isPasswordVisible: !isPasswordVisible,
    }));
  };

  function openScoutingPage() {
    window.open('https://beascout.scouting.org/', '_blank');
  }

  function openHelpPage() {
    window.open('https://help.scoutbook.scouting.org/', '_blank');
  }

  const showMessageWarning = () => {
    const maintenanceDate = DateTime.fromISO(
      featureFlags.getFlag('MAINTENANCE_END_DATE') as string,
    );

    const now = DateTime.now();
    if (!maintenanceDate.isValid) return false;

    return maintenanceDate.startOf('day') > now.startOf('day');
  };

  const messageStack = (
    (featureFlags.getFlag('MAINTENANCE_MESSAGE') as string) || ''
  ).split('__');

  const { loading, offline, masqueradeMode, masqueradeMemberId } = props;
  const { login, password, isPasswordVisible } = state;
  const isValidFrom = isValidForm();
  const disabled = loading || offline || !isValidFrom;
  const toggleViewPasswordIcon = isPasswordVisible ? (
    <MdVisibilityOff
      id="qa_hidePassword"
      className={styles.toggleViewPasswordIcon}
      onClick={handleTogglePasswordVisibilityClick}
    />
  ) : (
    <MdVisibility
      id="qa_showPassword"
      className={styles.toggleViewPasswordIcon}
      onClick={handleTogglePasswordVisibilityClick}
    />
  );

  if (!featureFlags.getFlag('SBL_6815_HIDE_LOGIN_V2')) {
    return (
      <Layout className={styles.layout}>
        <Header className={styles.headerBar}>
          <div className={styles.headBarContent}>
            <div className={styles.ribbon}>
              <span>
                <BSALogo className={styles.BSAIcon} />
              </span>
            </div>
            <div className={styles.headBarBttns}>
              <ButtonV5
                type="link"
                size="small"
                className={styles.helpBttn}
                onClick={openHelpPage}
              >
                <FormattedMessage id="user.LoginPage.landing.helpsNTutorials" />
              </ButtonV5>
              <ButtonV5
                type="default"
                variant="solid"
                color="white"
                size="small"
                onClick={openScoutingPage}
              >
                <FormattedMessage id="shared.signup" />
              </ButtonV5>
            </div>
          </div>
        </Header>
        <Content>
          <div className={styles.contentContainer}>
            <div className={styles.formContainer}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <FleurDeLisIcon className={styles.logoIcon} />
                <Heading
                  level={3}
                  font="display-h1"
                  color="color-text-regular"
                  className={styles.formHeader}
                >
                  <FormattedMessage
                    id="user.LoginPage.loginToScoutbook"
                    values={{ br: <br />, appName }}
                  />
                </Heading>
                {showMessageWarning() && (
                  <div className={styles.warningContainer}>
                    <S size="3" className={styles.formTitleWarning}>
                      <FormattedMessage id="user.LoginPage.titleWarning" />
                    </S>

                    {messageStack.map((message, index) => (
                      <Text
                        paragraph
                        key={index}
                        font="body-caption"
                        className={styles.formWarning}
                      >
                        {message}
                      </Text>
                    ))}
                  </div>
                )}
                {props.onMasqueradeModeChange && (
                  <DevMasqueradeFields
                    masqueradeMode={masqueradeMode ?? false}
                    masqueradeMemberId={masqueradeMemberId ?? ''}
                    disabled={loading || offline}
                    onModeChange={props.onMasqueradeModeChange}
                    onMemberIdChange={id =>
                      props.onMasqueradeMemberIdChange?.(id)
                    }
                  />
                )}
                {!masqueradeMode && (
                  <>
                    <Input
                      type="text"
                      name="login"
                      id="qa_username"
                      size="large"
                      autoComplete="username"
                      disabled={loading || offline}
                      placeholder={intl.formatMessage({
                        id: 'user.LoginPage.username',
                      })}
                      className={styles.loginInput}
                      value={login}
                      onChange={handleInputChange}
                    />
                    <Input
                      type={isPasswordVisible ? 'text' : 'password'}
                      name="password"
                      id="qa_password"
                      autoComplete="current-password"
                      size="large"
                      disabled={loading || offline}
                      placeholder={intl.formatMessage({
                        id: 'user.LoginPage.password',
                      })}
                      className={styles.passwordInput}
                      value={password}
                      onChange={handleInputChange}
                      suffix={toggleViewPasswordIcon}
                      ref={passwordInputRef}
                    />
                  </>
                )}
                {captchaEnabled && googleSiteKey && (
                  <Recaptcha
                    ref={captchaRef}
                    size="invisible"
                    sitekey={googleSiteKey}
                  />
                )}
                {offline ? (
                  <OfflineIcon className={styles.offlineIcon} />
                ) : (
                  <Button
                    htmlType="submit"
                    id="qa_login"
                    type="primary"
                    size="large"
                    shadow
                    disabled={disabled}
                    loading={loading}
                    className={styles.loginButton}
                  >
                    <FormattedMessage id="user.LoginPage.login" />
                  </Button>
                )}
                <span className={styles.passwordReminder}>
                  <a
                    href={RECOVER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage id="user.LoginPage.forgotUsername" />
                    {' / '}
                    <FormattedMessage id="user.LoginPage.forgotPassword" />
                  </a>
                </span>
                <Row justify="center" className={styles.externalLoginButtons}>
                  <Col span={24}>
                    <GoogleSignInButton />
                  </Col>
                </Row>
                <Row justify="center" className={styles.externalLoginButtons}>
                  <Col span={24}>
                    <AppleSignInButton />
                  </Col>
                </Row>
                {API_SWITCH_DEV && <ApiSelector />}
                <FlexSpacer />
                <S size="5" className={styles.appVersion}>
                  v{VERSION}
                </S>
              </form>
            </div>
            <div className={styles.landingContainer}>
              <div className={styles.contentInfoContainer}>
                <img src={scoutbookapp} className={styles.image} />
                <div className={styles.info}>
                  <div className={styles.logoTitle}>
                    <BSALogo />
                    <span>
                      <Heading font="display-h3" color="color-text-regular">
                        <span style={{ fontWeight: 'bolder' }}>SCOUT</span>{' '}
                        <span style={{ fontWeight: 'normal' }}>BOOK Plus</span>
                      </Heading>
                    </span>
                  </div>
                  <Text paragraph>
                    <FormattedMessage id="user.LoginPage.landing.sbp1" />
                  </Text>
                  <Text paragraph>
                    <FormattedMessage
                      id="user.LoginPage.landing.sbp2"
                      values={{
                        link: (
                          <a
                            href="https://help.scoutbook.scouting.org/knowledge-base/getting-a-unit-started-in-scoutbook/"
                            rel="noreferrer"
                            target={'_blank'}
                          >
                            {' find out how to set up a unit in Scoutbook'}
                          </a>
                        ),
                      }}
                    />
                  </Text>
                </div>
              </div>
              <div className={styles.contentInfoContainer}>
                <img src={scoutbookapp2} className={styles.image} />
                <div className={styles.info}>
                  <Heading font="display-h3" color="color-text-regular">
                    <FormattedMessage id="user.LoginPage.landing.forTroopTitle" />
                  </Heading>
                  <Text paragraph>
                    <FormattedMessage id="user.LoginPage.landing.sbp3" />
                  </Text>
                  <Text paragraph>
                    <FormattedMessage id="user.LoginPage.landing.sbp4" />
                  </Text>
                </div>
              </div>
              <div className={styles.contentInfoContainer}>
                <img src={scoutbookapp4} className={styles.image} />
                <div className={styles.info}>
                  <Heading font="display-h3" color="color-text-regular">
                    <FormattedMessage id="user.LoginPage.landing.forParentsTitle" />
                  </Heading>
                  <Text paragraph>
                    <FormattedMessage id="user.LoginPage.landing.sbp5" />
                  </Text>
                  <Text paragraph>
                    <FormattedMessage
                      id="user.LoginPage.landing.sbp6"
                      values={{
                        link1: (
                          <a
                            href={
                              'https://itunes.apple.com/us/app/scouting/id1218464473?mt=8'
                            }
                            rel="noreferrer"
                            target={'_blank'}
                          >
                            {' Apple '}
                          </a>
                        ),
                        link2: (
                          <a
                            href="https://play.google.com/store/apps/details?id=com.bsa.sbm&hl=en"
                            rel="noreferrer"
                            target="_blank"
                          >
                            {' Android '}
                          </a>
                        ),
                      }}
                    />
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </Content>
        <Footer className={styles.footer}>
          <div className={styles.footerInfo}>
            <Heading font="display-h3" color="color-white">
              <FormattedMessage id="user.LoginPage.landing.helpsNTutorials" />
            </Heading>
            <Text paragraph color="color-white">
              <FormattedMessage
                id="user.LoginPage.landing.sbp7"
                values={{
                  link: (
                    <a
                      href="https://help.scoutbook.scouting.org/"
                      rel="noreferrer"
                      target="_blank"
                    >
                      <strong>help.scoutbook.scouting.org</strong>
                    </a>
                  ),
                }}
              />
            </Text>
          </div>
        </Footer>
      </Layout>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.bg} />
      <Card className={styles.card} shadow>
        <form className={styles.form} onSubmit={handleSubmit}>
          <FleurDeLisIcon className={styles.logoIcon} />
          <T size="2" className={styles.formHeader}>
            <FormattedMessage
              id="user.LoginPage.loginToScoutbook"
              values={{ br: <br />, appName }}
            />
          </T>
          {showMessageWarning() && (
            <div className={styles.warningContainer}>
              <S size="3" className={styles.formTitleWarning}>
                <FormattedMessage id="user.LoginPage.titleWarning" />
              </S>
              <S size="4" className={styles.formWarning}>
                {featureFlags.getFlag('MAINTENANCE_MESSAGE')}
              </S>
            </div>
          )}
          {props.onMasqueradeModeChange && (
            <DevMasqueradeFields
              masqueradeMode={masqueradeMode ?? false}
              masqueradeMemberId={masqueradeMemberId ?? ''}
              disabled={loading || offline}
              onModeChange={props.onMasqueradeModeChange}
              onMemberIdChange={id => props.onMasqueradeMemberIdChange?.(id)}
            />
          )}
          {!masqueradeMode && (
            <>
              <Input
                type="text"
                name="login"
                id="qa_username"
                size="large"
                autoComplete="username"
                disabled={loading || offline}
                placeholder={intl.formatMessage({
                  id: 'user.LoginPage.username',
                })}
                className={styles.loginInput}
                value={login}
                onChange={handleInputChange}
              />
              <Input
                type={isPasswordVisible ? 'text' : 'password'}
                name="password"
                id="qa_password"
                autoComplete="current-password"
                size="large"
                disabled={loading || offline}
                placeholder={intl.formatMessage({
                  id: 'user.LoginPage.password',
                })}
                className={styles.passwordInput}
                value={password}
                onChange={handleInputChange}
                suffix={toggleViewPasswordIcon}
                ref={passwordInputRef}
              />
            </>
          )}
          {captchaEnabled && googleSiteKey && (
            <Recaptcha
              ref={captchaRef}
              size="invisible"
              sitekey={googleSiteKey}
            />
          )}
          {offline ? (
            <OfflineIcon className={styles.offlineIcon} />
          ) : (
            <Button
              htmlType="submit"
              id="qa_login"
              type="primary"
              size="large"
              shadow
              disabled={disabled}
              loading={loading}
              className={styles.loginButton}
            >
              <FormattedMessage id="user.LoginPage.login" />
            </Button>
          )}
          <span className={styles.passwordReminder}>
            <a href={RECOVER_URL} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="user.LoginPage.forgotUsername" />
              {' / '}
              <FormattedMessage id="user.LoginPage.forgotPassword" />
            </a>
          </span>
          <Row justify="center" className={styles.externalLoginButtons}>
            <Col span={24}>
              <GoogleSignInButton />
            </Col>
          </Row>
          <Row justify="center" className={styles.externalLoginButtons}>
            <Col span={24}>
              <AppleSignInButton />
            </Col>
          </Row>
          {API_SWITCH_DEV && <ApiSelector />}
          <span className={styles.formNewUserText}>
            <FormattedMessage id="user.LoginPage.newToScouting" />
          </span>
          <Button
            id="qa_createAccount"
            onClick={openScoutingPage}
            ghost
            noBorder
            size="small"
            fitText
            className={styles.formNewUserButton}
          >
            <FormattedMessage id="user.LoginPage.createAccount" />
          </Button>
          <FlexSpacer />
          <S size="5" className={styles.appVersion}>
            v{VERSION}
          </S>
        </form>
      </Card>
      <ScoutbookAd className={styles.ad} />
    </div>
  );
};

export function useOnLogin() {
  const dispatch = useDispatch();
  const { login } = useLogin();

  const legacyLogin = useCallback(
    (username: string, password: string) =>
      dispatch(loginRequest(username, password)),
    [dispatch],
  );

  return featureFlags.getFlag('SBL_7006_RECAPTCHA_TOKEN') ? login : legacyLogin;
}

function LoginContainer() {
  const loginLoading = useSelector(loginLoadingSel);
  const loading_user = useSelector(userDataLoadingSel);
  const offline = useSelector(offlineSel);
  const onLogin = useOnLogin();

  return (
    <LoginPage
      loading={loginLoading || loading_user}
      offline={offline}
      onLogin={onLogin}
    />
  );
}

function DevLoginContainer() {
  const loginLoading = useSelector(loginLoadingSel);
  const loading_user = useSelector(userDataLoadingSel);
  const offline = useSelector(offlineSel);
  const onLogin = useOnLogin();

  const devMasquerade = useDevMasquerade();

  const handleLogin = useCallback(
    (login: string, password: string, recaptchaToken?: string) => {
      if (devMasquerade.masqueradeMode) {
        devMasquerade.handleLogin(onLogin, recaptchaToken);
        return;
      }
      onLogin(login, password, recaptchaToken);
    },
    [onLogin, devMasquerade],
  );

  const loading =
    loginLoading || loading_user || devMasquerade.masqueradeLoading;

  return (
    <LoginPage
      loading={loading}
      offline={offline}
      onLogin={handleLogin}
      masqueradeMode={devMasquerade.masqueradeMode}
      masqueradeMemberId={devMasquerade.masqueradeMemberId}
      onMasqueradeModeChange={devMasquerade.setMasqueradeMode}
      onMasqueradeMemberIdChange={devMasquerade.setMasqueradeMemberId}
    />
  );
}

export default __DEV__ && DEV_MASQUERADE_LOGIN
  ? DevLoginContainer
  : LoginContainer;
